import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FiHome } from 'react-icons/fi';
import DashboardDataTable from './DashboardDataTable';
import { GapNotice } from './states';
import { bucketByDay } from './dateRange';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';

const TAB_DEFS = [
  { id: 'growth', label: 'Growth' },
  { id: 'pms', label: 'PMS' },
  { id: 'hosts', label: 'Hosts' },
  { id: 'drivers', label: 'Drivers' },
  { id: 'orders', label: 'Orders' },
  { id: 'properties', label: 'Properties' },
];

const CHART_COLORS = ['#6366f1', '#0ea5e9', '#14b8a6', '#f59e0b', '#f43f5e', '#8b5cf6'];

const tooltipStyle = {
  fontSize: 11,
  borderRadius: 6,
  border: '1px solid #e4e4e7',
  background: '#ffffff',
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
};

function ChartFrame({ title, subtitle, right, children }) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-zinc-200 bg-white p-3">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div>
          <h4 className="text-[12px] font-semibold text-zinc-900">{title}</h4>
          {subtitle ? <p className="text-[11px] text-zinc-500">{subtitle}</p> : null}
        </div>
        {right}
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

export default function AnalyticsTabs({
  activeTab,
  onTabChange,
  range,
  hosts,
  filteredHosts,
  orders,
  properties,
  propertiesAvailable,
  hostOrderStats,
  drivers = [],
  newDrivers = [],
  driverWindows = { wtd: 0, mtd: 0, ytd: 0, total: 0 },
  onSelectHost,
  onSelectProperty,
  onSelectOrder,
  onSelectPms,
}) {
  const [internalTab, setInternalTab] = useState('growth');
  const tab = activeTab || internalTab;
  const setTab = onTabChange || setInternalTab;

  // -- Growth: line chart of newly onboarded hosts/day in range
  const growthSeries = useMemo(() => {
    return bucketByDay(filteredHosts, (h) => h.createdAt, range.start, range.end);
  }, [filteredHosts, range]);

  // -- PMS: bar chart of host count per PMS
  const pmsData = useMemo(() => {
    const m = new Map();
    filteredHosts.forEach((h) => {
      const k = h.pmsType || 'unknown';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([k, count]) => ({ id: k, label: PMS_LABELS[k] || k, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredHosts]);

  // -- Drivers: per-day signup line in active range
  const driverSeries = useMemo(
    () => bucketByDay(drivers, (d) => d.createdAt, range.start, range.end),
    [drivers, range]
  );

  const hostRows = useMemo(() => {
    return filteredHosts.map((h) => {
      const stats = hostOrderStats?.get(String(h._id)) || { orders: 0, revenue: 0, aov: 0 };
      return {
        ...h,
        orderCount: stats.orders,
        revenue: stats.revenue,
        aov: stats.aov,
      };
    });
  }, [filteredHosts, hostOrderStats]);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center gap-1 border-b border-zinc-100 px-2">
        {TAB_DEFS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-[12px] font-medium transition ${
              tab === t.id
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-12">
        {tab === 'growth' ? (
          <>
            <div className="lg:col-span-5" style={{ minHeight: 280 }}>
              <ChartFrame
                title="New host signups"
                subtitle="By day, in selected range"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthSeries} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartFrame>
            </div>
            <div className="lg:col-span-7" style={{ minHeight: 280 }}>
              <DashboardDataTable
                rows={hostRows}
                onRowClick={onSelectHost}
                searchPlaceholder="Search hosts in growth view…"
                initialSort={{ key: 'createdAt', dir: 'desc' }}
                columns={[
                  { key: 'name', header: 'Host', searchable: true, sortable: true, accessor: (r) => r.name,
                    cell: (r) => (
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                          {(r.name || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-zinc-900">{r.name}</p>
                          <p className="truncate text-[10px] text-zinc-500">{r.email || '—'}</p>
                        </div>
                      </div>
                    ),
                  },
                  { key: 'createdAt', header: 'Created', sortable: true, sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
                    cell: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
                  },
                  { key: 'pmsType', header: 'PMS', sortable: true, accessor: (r) => PMS_LABELS[r.pmsType] || '—' },
                  { key: 'isNew', header: 'Type', sortable: true, accessor: (r) => (r.isNew ? 'New' : 'Legacy'),
                    cell: (r) => (
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                        r.isNew ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        {r.isNew ? 'New' : 'Legacy'}
                      </span>
                    ),
                  },
                  { key: 'propertyCount', header: 'Props', align: 'right', sortable: true, accessor: (r) => r.propertyCount },
                ]}
              />
            </div>
          </>
        ) : null}

        {tab === 'pms' ? (
          <>
            <div className="lg:col-span-5" style={{ minHeight: 280 }}>
              <ChartFrame
                title="Hosts by PMS"
                subtitle="Distribution across integrations"
                right={pmsData.some((d) => d.id === 'unknown') ? <GapNotice label="Awaiting backend §A" /> : null}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pmsData} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {pmsData.map((d, i) => (
                        <Cell key={d.id} fill={CHART_COLORS[i % CHART_COLORS.length]} cursor="pointer" onClick={() => onSelectPms?.(d.id)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartFrame>
            </div>
            <div className="lg:col-span-7" style={{ minHeight: 280 }}>
              <DashboardDataTable
                rows={pmsData}
                onRowClick={(d) => onSelectPms?.(d.id)}
                searchPlaceholder="Search PMS…"
                initialSort={{ key: 'count', dir: 'desc' }}
                columns={[
                  { key: 'label', header: 'PMS', searchable: true, sortable: true,
                    cell: (r) => (
                      <span className="font-semibold text-zinc-900">{r.label}</span>
                    ),
                  },
                  { key: 'count', header: 'Hosts', align: 'right', sortable: true,
                    cell: (r) => fmtNumber(r.count),
                  },
                  { key: 'share', header: 'Share', align: 'right', sortable: true,
                    sortAccessor: (r) => r.count,
                    cell: (r) => {
                      const total = pmsData.reduce((s, x) => s + x.count, 0);
                      return total ? `${Math.round((r.count / total) * 100)}%` : '0%';
                    },
                  },
                ]}
              />
            </div>
          </>
        ) : null}

        {tab === 'hosts' ? (
          <div className="lg:col-span-12" style={{ minHeight: 320 }}>
            <DashboardDataTable
              rows={hostRows}
              onRowClick={onSelectHost}
              searchPlaceholder="Search hosts by name, email, company…"
              initialSort={{ key: 'orderCount', dir: 'desc' }}
              columns={[
                { key: 'name', header: 'Host', searchable: true, sortable: true, accessor: (r) => r.name,
                  cell: (r) => (
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                        {(r.name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-zinc-900">{r.name}</p>
                        <p className="truncate text-[10px] text-zinc-500">{r.email || '—'}</p>
                      </div>
                    </div>
                  ),
                },
                { key: 'pmsType', header: 'PMS', sortable: true, accessor: (r) => PMS_LABELS[r.pmsType] || '—' },
                { key: 'propertyCount', header: 'Props', align: 'right', sortable: true, accessor: (r) => r.propertyCount },
                { key: 'orderCount', header: 'Orders', align: 'right', sortable: true, accessor: (r) => r.orderCount },
                { key: 'aov', header: 'AOV', align: 'right', sortable: true, accessor: (r) => r.aov,
                  cell: (r) => fmtMoney(r.aov),
                },
                { key: 'revenue', header: 'Volume', align: 'right', sortable: true, accessor: (r) => r.revenue,
                  cell: (r) => fmtMoney(r.revenue),
                },
                { key: 'isNew', header: 'Type', sortable: true, accessor: (r) => (r.isNew ? 'New' : 'Legacy'),
                  cell: (r) => (
                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                      r.isNew ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
                    }`}>
                      {r.isNew ? 'New' : 'Legacy'}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        ) : null}

        {tab === 'orders' ? (
          <div className="lg:col-span-12" style={{ minHeight: 320 }}>
            <DashboardDataTable
              rows={orders}
              onRowClick={onSelectOrder}
              searchPlaceholder="Search orders by id, store, status…"
              initialSort={{ key: 'createdAt', dir: 'desc' }}
              columns={[
                { key: 'uniqueId', header: 'Order', searchable: true, sortable: true,
                  accessor: (r) => r.uniqueId || r._id,
                  cell: (r) => (
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-zinc-900">{r.uniqueId || String(r._id).slice(-6)}</p>
                      <p className="truncate text-[10px] text-zinc-500">{r?.property?.address?.line1 || '—'}</p>
                    </div>
                  ),
                },
                { key: 'createdAt', header: 'Created', sortable: true,
                  sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
                  cell: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—',
                },
                { key: 'status', header: 'Status', sortable: true, searchable: true,
                  cell: (r) => (
                    <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700">
                      {r.status}
                    </span>
                  ),
                },
                { key: 'storeLocation', header: 'Store', searchable: true, sortable: true,
                  accessor: (r) => r.storeLocation || '',
                },
                { key: 'revenue', header: 'Revenue', align: 'right', sortable: true,
                  sortAccessor: (r) => r._revenue || 0,
                  accessor: (r) => r._revenue || 0,
                  cell: (r) => fmtMoney(r._revenue || 0),
                },
              ]}
            />
          </div>
        ) : null}

        {tab === 'drivers' ? (
          <>
            <div className="lg:col-span-12 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { id: 'wtd', label: 'WTD', value: driverWindows.wtd, sub: 'Sun → today' },
                { id: 'mtd', label: 'MTD', value: driverWindows.mtd, sub: 'Month to date' },
                { id: 'ytd', label: 'YTD', value: driverWindows.ytd, sub: 'Year to date' },
                { id: 'total', label: 'All time', value: driverWindows.total, sub: 'Total acquired' },
              ].map((w) => (
                <div key={w.id} className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{w.label}</p>
                  <p className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-900">{fmtNumber(w.value)}</p>
                  <p className="text-[10px] text-zinc-500">{w.sub}</p>
                </div>
              ))}
            </div>
            <div className="lg:col-span-5" style={{ minHeight: 280 }}>
              <ChartFrame
                title="Driver acquisition"
                subtitle="Signups by day, in selected range"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={driverSeries} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartFrame>
            </div>
            <div className="lg:col-span-7" style={{ minHeight: 280 }}>
              <DashboardDataTable
                rows={drivers}
                searchPlaceholder="Search drivers by name, email, market…"
                initialSort={{ key: 'createdAt', dir: 'desc' }}
                columns={[
                  { key: 'name', header: 'Driver', searchable: true, sortable: true, accessor: (r) => r.name,
                    cell: (r) => (
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                          {(r.name || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-zinc-900">{r.name}</p>
                          <p className="truncate text-[10px] text-zinc-500">{r.email || '—'}</p>
                        </div>
                      </div>
                    ),
                  },
                  { key: 'createdAt', header: 'Acquired', sortable: true,
                    sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
                    cell: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
                  },
                  { key: 'market', header: 'Market', sortable: true, searchable: true, accessor: (r) => r.market || '—' },
                  { key: 'active', header: 'Status', sortable: true, accessor: (r) => (r.active ? 'Active' : 'Inactive'),
                    cell: (r) => (
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                        r.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <p className="lg:col-span-12 text-[10px] text-zinc-500">
              Driver <code>createdAt</code> is set once at signup by Mongoose <code>{'{ timestamps: true }'}</code> and is not mutated by profile or status changes — safe as the acquisition date.
            </p>
          </>
        ) : null}

        {tab === 'properties' ? (
          <div className="lg:col-span-12" style={{ minHeight: 320 }}>
            {propertiesAvailable ? (
              <DashboardDataTable
                rows={properties}
                onRowClick={onSelectProperty}
                searchPlaceholder="Search properties by address, city…"
                initialSort={{ key: 'createdAt', dir: 'desc' }}
                columns={[
                  { key: 'address', header: 'Address', searchable: true, sortable: true,
                    accessor: (r) => `${r?.address?.line1 || ''} ${r?.address?.city || ''}`,
                    cell: (r) => (
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-zinc-900">{r?.address?.line1 || r?.listingTitle || 'Property'}</p>
                        <p className="truncate text-[10px] text-zinc-500">
                          {r?.address?.city || ''}{r?.address?.state ? `, ${r.address.state}` : ''}
                        </p>
                      </div>
                    ),
                  },
                  { key: 'createdAt', header: 'Created', sortable: true,
                    sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
                    cell: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
                  },
                  { key: 'propertySource', header: 'Source', sortable: true, searchable: true,
                    accessor: (r) => PMS_LABELS[r.propertySource] || r.propertySource || '—',
                  },
                ]}
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-8 text-center">
                <div>
                  <FiHome className="mx-auto h-6 w-6 text-amber-500" />
                  <p className="mt-2 text-sm font-semibold text-amber-900">Property timestamps unavailable</p>
                  <p className="mt-1 text-xs text-amber-700">
                    Backend proposal §B (extending <code>GET /properties</code> with <code>propertySource</code> + <code>createdAt</code>)
                    is required to power this tab. Hosts &amp; Orders tabs are fully populated in the meantime.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
